/**
 * What stops a plan version's ranks from being final — worded for a person.
 *
 * A Plan of Record gives every item it commits to one rank, shared with no
 * other (owner ruling, 2026-10-13; migration 819). The database is the
 * authority: `plan_version_rank_problems` finds the problems and
 * `authorize_por` refuses on them. This file only turns those rows into the
 * sentence shown before the click (build contract §2.8 — name the item and the
 * fix before the rule).
 */

/** One row of the `plan_version_rank_problems` database function. */
export interface PlanRankProblemRow {
  problem: string;
  rank: number | null;
  item_ids: string[] | null;
  item_titles: string[] | null;
}

function quoteAll(titles: readonly string[]): string {
  return titles.map((t) => `"${t}"`).join(', ');
}

/**
 * One clause per problem: `"A", "B" share rank 3` or `"C" has no rank`.
 * Unknown problem kinds are skipped rather than guessed at.
 */
export function describeRankProblems(rows: readonly PlanRankProblemRow[]): string[] {
  const clauses: string[] = [];
  for (const row of rows) {
    const titles = row.item_titles ?? [];
    if (titles.length === 0) continue;
    if (row.problem === 'shared_rank' && row.rank != null) {
      clauses.push(`${quoteAll(titles)} share rank ${row.rank}`);
    } else if (row.problem === 'missing_rank') {
      clauses.push(`${quoteAll(titles)} ${titles.length === 1 ? 'has' : 'have'} no rank`);
    }
  }
  return clauses;
}

/** How many items are caught up in the problems (an item is counted once). */
export function countItemsWithRankProblems(rows: readonly PlanRankProblemRow[]): number {
  const ids = new Set<string>();
  for (const row of rows) for (const id of row.item_ids ?? []) ids.add(id);
  return ids.size;
}

export const RANKS_NOT_FINAL_FIX = 'Give each item its own rank in the plan, then authorize again.';
